/* istanbul ignore file */

import { AxisOrientation, Handedness } from '@partful/features/configurations/model/types';
import type {
  CADJobStatusOptions,
  KitsJobStatusOptions,
  POSTCADAPIResponse,
  ProductListAPIResponse,
} from '@partful/shared-types';
import { CADUploadJob, SalesBomJob } from '@providers/job/types';
import { Auth } from 'aws-amplify';
import axios from 'axios';
import { isAuthorised } from 'src/utils/cognito';
import { error as logError } from './log';

export const DEFAULT_JOB_TIMEOUT = 18000000; // 5 hours in milliseconds

export const productInventoryEndpoints = {
  configurations: '/configurations',
  products: '/products',
  partTrees: '/part-trees',
  contentProcessing: {
    root: '/content-processing-jobs',
    cad: '/content-processing-jobs/cad',
    sbom: '/content-processing-jobs/sales-bom',
    partsCategories: '/content-processing-jobs/part-categories',
    kits: '/content-processing-jobs/kits',
  },
  partCategories: '/part-categories',
} as const;

export interface ZipFileContent {
  path: string;
  size: number;
}

export interface DesignAttributePreview {
  name: string;
  values: string[];
}

export enum ColourMode {
  COLOUR = 'COLOUR',
  GREYSCALE = 'GREYSCALE',
}

export enum PostProcessing {
  ADVANCED = 'ADVANCED',
  BASIC = 'BASIC',
}

export const ConfigurationState = {
  CREATING: 'CREATING',
  CREATED: 'CREATED',
  DRAFT: 'DRAFT',
  LIVE: 'LIVE',
  DELETED: 'DELETED',
} as const;

export type ConfigurationStatus = (typeof ConfigurationState)[keyof typeof ConfigurationState];

export type Configuration = {
  hierarchyDesignAttributesPreview: DesignAttributePreview[];
  status: ConfigurationStatus;
  colourMode?: ColourMode;
  postProcessing?: PostProcessing;
  name: string;
  productCategories?: string[];
  parentPID: string;
  thumbnail?: string;
  thumbnailUrl?: string;
  axisOrientation?: AxisOrientation;
  handedness?: Handedness;
} & AttributeNames;

export interface AttributeNames {
  partNumberDesignAttribute: string;
  partNameDesignAttribute: string;
  cadJobRunID: string; // Not saved to configuration, needed to trigger initial category ingest
}

export const getToken = async () => {
  try {
    const session = await Auth.currentSession();
    const idToken = session.getIdToken();
    /* istanbul ignore next */
    if (!isAuthorised(session)) {
      Auth.signOut();
      throw Error('No access');
    }
    return idToken.getJwtToken();
  } catch (err) {
    /* istanbul ignore next */
    throw Error('Error getting token');
  }
};
const getProductInventoryService = async ({ contentType }: { contentType?: string } = {}) => {
  const url = process.env.REACT_APP_PRODUCT_INVENTORY_SERVICE_BASE_URL;
  return axios.create({
    baseURL: url,
    headers: { Authorization: `Bearer ${await getToken()}`, 'Content-Type': contentType },
  });
};

export const getConfigurations = async (accountId: string): Promise<ProductListAPIResponse | undefined> => {
  try {
    const productInventoryService = await getProductInventoryService();
    const configurations: ProductListAPIResponse = (
      await productInventoryService.get(`${productInventoryEndpoints.configurations}?account=${accountId}`)
    ).data;

    return configurations;
  } catch (error) /* istanbul ignore next */ {
    logError(error as Error);
    throw error;
  }
};

/* istanbul ignore next */
export const getConfiguration = async (PID: string, accountId: string): Promise<Configuration | undefined> => {
  const productInventoryService = await getProductInventoryService();

  try {
    return (
      await productInventoryService.get(`${productInventoryEndpoints.configurations}/${PID}?account=${accountId}`)
    ).data.payload;
  } catch (error) {
    logError(error as Error);
    throw error;
  }
};
/* istanbul ignore next */
export const patchConfiguration = async (
  PID: string,
  body: Partial<Configuration>,
  accountId: string
): Promise<Configuration | undefined> => {
  const productInventoryService = await getProductInventoryService({ contentType: 'text/plain' });

  try {
    return (
      await productInventoryService.patch(
        `${productInventoryEndpoints.configurations}/${PID}?account=${accountId}`,
        body
      )
    ).data;
  } catch (error) /* istanbul ignore next */ {
    logError(error as Error);
    throw error;
  }
};

export interface ErrorBody {
  message: string;
  source: string;
}

export interface ProcessingJobError extends ErrorBody {}

export interface GETCADJobResponse {
  runID: string;
  status: CADJobStatusOptions;
  productName: string;
  fileDetails: ZipFileContent[];
  created: string; // UTC time
  selectedGeneralAssembly?: string;
  configurationPID?: string;
  productPID?: string;
  jobType: 'CAD';
}

export const getCADJob = async (runID: string, accountId: string): Promise<GETCADJobResponse | undefined> => {
  const url = process.env.REACT_APP_PRODUCT_INVENTORY_SERVICE_BASE_URL;
  const productInventoryService = axios.create({
    baseURL: url,
    headers: { Authorization: `Bearer ${await getToken()}` },
  });

  try {
    return (
      await productInventoryService.get(
        `${productInventoryEndpoints.contentProcessing.cad}/${runID}?account=${accountId}`
      )
    ).data.payload;
  } catch (error) /* istanbul ignore next */ {
    logError(error as Error);
    throw error;
  }
};

export type PATCHCADJobResponse = {
  url: string;
  runID: string;
};

export const patchCADJob = async (
  runID: string,
  accountId: string,
  data: {
    status?: CADJobStatusOptions;
    selectedGeneralAssembly?: string;
    errors?: ProcessingJobError[];
  }
): Promise<PATCHCADJobResponse> => {
  const url = process.env.REACT_APP_PRODUCT_INVENTORY_SERVICE_BASE_URL;
  const productInventoryService = axios.create({
    baseURL: url,
    headers: { Authorization: `Bearer ${await getToken()}`, 'Content-Type': 'text/plain' },
  });

  try {
    return (
      await productInventoryService.patch(
        `${productInventoryEndpoints.contentProcessing.cad}/${runID}?account=${accountId}`,
        data
      )
    ).data.payload;
  } catch (error) /* istanbul ignore next */ {
    logError(error as Error);
    throw error;
  }
};

export const postCAD = async (productName: string, accountId: string): Promise<POSTCADAPIResponse> => {
  const productInventoryService = await getProductInventoryService({ contentType: 'text/plain' });

  try {
    return (
      await productInventoryService.post(
        `${productInventoryEndpoints.contentProcessing.cad}?account=${accountId}`,
        JSON.stringify({ productName })
      )
    ).data.payload;
  } catch (error) /* istanbul ignore next */ {
    logError(error as Error);
    throw error;
  }
};

export interface POSTSBOMResponse {
  url: string;
  runID: string;
  uploadURL: string;
}

export const postSBOM = async (configurationPID: string, accountId: string): Promise<POSTSBOMResponse> => {
  const productInventoryService = await getProductInventoryService({ contentType: 'text/plain' });

  try {
    const sBOMUpload = await productInventoryService.post(
      `${productInventoryEndpoints.contentProcessing.sbom}?account=${accountId}`,
      JSON.stringify({ configurationPID })
    );
    return sBOMUpload.data.payload;
  } catch (error) {
    logError(error as Error);
    throw error;
  }
};

export type PATCHSalesBOMJobResponse = {
  url: string;
  runID: string;
};

export const patchSalesBOMJob = async (
  runID: string,
  accountId: string,
  data: {
    status?: SalesBOMJobStatus;
    errors?: ProcessingJobError[];
  }
): Promise<PATCHSalesBOMJobResponse> => {
  const productInventoryService = await getProductInventoryService({ contentType: 'text/plain' });

  try {
    return (
      await productInventoryService.patch(
        `${productInventoryEndpoints.contentProcessing.sbom}/${runID}?account=${accountId}`,
        data
      )
    ).data.payload;
  } catch (error) /* istanbul ignore next */ {
    logError(error as Error);
    throw error;
  }
};

export interface POSTKitsJobResponse {
  runID: string;
  uploadURL: string;
}

export const postKitsJob = async ({
  configurationPID,
  accountId,
}: {
  configurationPID: string;
  accountId: string;
}): Promise<POSTKitsJobResponse> => {
  const productInventoryService = await getProductInventoryService({ contentType: 'text/plain' });

  try {
    const kitsUpload = await productInventoryService.post(
      `${productInventoryEndpoints.contentProcessing.kits}?account=${accountId}`,
      JSON.stringify({ configurationPID })
    );
    return kitsUpload.data.payload;
  } catch (error) {
    logError(error as Error);
    throw error;
  }
};

export interface POSTKitsIngestJobResponse {
  runID: string;
}

export const postKitsIngest = async ({
  accountId,
  runID,
}: {
  accountId: string;
  runID: string;
}): Promise<POSTKitsIngestJobResponse> => {
  const productInventoryService = await getProductInventoryService({ contentType: 'text/plain' });
  const url = new URL(
    `${process.env.REACT_APP_PRODUCT_INVENTORY_SERVICE_BASE_URL}${productInventoryEndpoints.contentProcessing.kits}/${runID}/start-ingest`
  );
  url.searchParams.set('account', accountId);

  try {
    const kitsStartIngest = await productInventoryService.post(`${url.pathname}${url.search}`);
    return kitsStartIngest.data.payload;
  } catch (error) {
    logError(error as Error);
    throw error;
  }
};

export interface PATCHKitsJobResponse {
  runID: string;
  url: string;
}

export const patchKitsJob = async (
  runID: string,
  accountId: string,
  data: {
    status?: KitsJobStatusOptions;
    errors?: ProcessingJobError[];
  }
): Promise<PATCHKitsJobResponse> => {
  const productInventoryService = await getProductInventoryService({ contentType: 'text/plain' });

  try {
    return (
      await productInventoryService.patch(
        `${productInventoryEndpoints.contentProcessing.kits}/${runID}?account=${accountId}`,
        data
      )
    ).data.payload;
  } catch (error) /* istanbul ignore next */ {
    logError(error as Error);
    throw error;
  }
};

export interface GETKitsJobResponse {
  url: string;
  runID: string;
  status: KitsJobStatusOptions;
  created: string;
  errors: ErrorBody[] | null;
  jobType: 'Kits';
}

export interface POSTPartsCategoriesResponse {
  runID: string;
  uploadURL: string;
}

export const postPartsCategoriesJob = async ({
  configurationPID,
  accountId,
}: {
  configurationPID: string;
  accountId: string;
}): Promise<POSTPartsCategoriesResponse> => {
  const productInventoryService = await getProductInventoryService({ contentType: 'text/plain' });

  try {
    const partsCategoriesUpload = await productInventoryService.post(
      `${productInventoryEndpoints.contentProcessing.partsCategories}?account=${accountId}`,
      JSON.stringify({ configurationPID })
    );
    return partsCategoriesUpload.data.payload;
  } catch (error) {
    logError(error as Error);
    throw error;
  }
};

export type PartCategoriesStatusOptions =
  | 'IN_PROGRESS_UPLOAD'
  | 'IN_PROGRESS_INGEST'
  | 'IN_PROGRESS_3D_CURATION'
  | 'IN_PROGRESS_INDEXING'
  | 'IN_PROGRESS_PREVIEW'
  // @deprecated - use IN_PROGRESS_PREVIEW instead
  | 'COMPLETED_READY_FOR_PREVIEW'
  | 'COMPLETED'
  | 'FAILED'
  | 'CANCELLED_BY_USER';

export interface GetPartsCategoriesJobResponse {
  url: string;
  runID: string;
  productPID: string;
  configurationPID: string;
  status: PartCategoriesStatusOptions;
  created: string;
  errorMessage?: string;
  errors: ErrorBody[] | null;
  jobType: 'PartsCategories';
}

export const getPartsCategoriesJob = async (
  runID: string,
  accountId: string
): Promise<GetPartsCategoriesJobResponse> => {
  const productInventoryService = await getProductInventoryService();

  try {
    return (
      await productInventoryService.get(
        `${productInventoryEndpoints.contentProcessing.partsCategories}/${runID}?account=${accountId}`
      )
    ).data.payload;
  } catch (error) {
    logError(error as Error);
    throw error;
  }
};

interface PartCategoriesJob {
  runID: string;
  status: PartCategoriesStatusOptions;
  productPID: string;
  configurationPID: string;
  created: string;
  jobType: 'PartCategories';
}

export interface GETLatestPartsCategoriesJobResponse {
  url: string;
  jobs: PartCategoriesJob[];
}

export const getLatestPartCategoriesJob = async ({
  accountId,
  configurationPID,
}: {
  accountId: string;
  configurationPID: string;
}): Promise<Array<GetPartsCategoriesJobResponse>> => {
  const productInventoryService = await getProductInventoryService();

  try {
    return (
      await productInventoryService.get(
        `${productInventoryEndpoints.contentProcessing.partsCategories}?account=${accountId}&configurationPID=${configurationPID}&latest=true`
      )
    ).data.payload.jobs;
  } catch (error) {
    logError(error as Error);
    throw error;
  }
};

export interface POSTPartsCategoriesIngestResponse {
  runID: string;
}

export const postPartsCategoriesIngest = async ({
  accountId,
  runID,
}: {
  runID: string;
  accountId: string;
}): Promise<POSTPartsCategoriesIngestResponse> => {
  const productInventoryService = await getProductInventoryService({ contentType: 'text/plain' });
  const url = new URL(
    `${process.env.REACT_APP_PRODUCT_INVENTORY_SERVICE_BASE_URL}${productInventoryEndpoints.contentProcessing.partsCategories}/${runID}/start-ingest`
  );
  url.searchParams.set('account', accountId);

  try {
    const partsCategoriesStartIngest = await productInventoryService.post(`${url.pathname}${url.search}`);
    return partsCategoriesStartIngest.data.payload;
  } catch (error) {
    logError(error as Error);
    throw error;
  }
};

export type PATCHPartCategoriesJobResponse = {
  url: string;
  runID: string;
};

export const patchPartsCategoriesJob = async ({
  accountId,
  runID,
  status,
  errors,
}: {
  accountId: string;
  runID: string;
  status?: PartCategoriesStatusOptions;
  errors?: ProcessingJobError[];
}): Promise<PATCHPartCategoriesJobResponse> => {
  const productInventoryService = await getProductInventoryService({ contentType: 'text/plain' });

  try {
    return (
      await productInventoryService.patch(
        `${productInventoryEndpoints.contentProcessing.partsCategories}/${runID}?account=${accountId}`,
        { status, errors }
      )
    ).data.payload;
  } catch (error) {
    logError(error as Error);
    throw error;
  }
};

export interface POSTSBOMIngestResponse {
  cadJobRunID: string;
  salesJobRunID: string;
}

export const postSBOMIngest = async (
  accountId: string,
  salesJobRunID: string,
  parameters: { cadJobRunID?: string; configurationPID?: string }
): Promise<POSTSBOMIngestResponse> => {
  const productInventoryService = await getProductInventoryService({ contentType: 'text/plain' });

  try {
    const sBOMIngest = await productInventoryService.post(
      `${productInventoryEndpoints.contentProcessing.sbom}/${salesJobRunID}/start-ingest?account=${accountId}`,
      JSON.stringify(parameters)
    );
    return sBOMIngest.data.payload;
  } catch (error) {
    logError(error as Error);
    throw error;
  }
};

export interface SBOMTemplateResponse {
  downloadURL: string;
}

export const getSBOMTemplate = async (
  accountId: string,
  configurationPID: string,
  productName: string
): Promise<SBOMTemplateResponse | undefined> => {
  const productInventoryService = await getProductInventoryService();

  try {
    return (
      await productInventoryService.get(
        `${productInventoryEndpoints.configurations}/${configurationPID}/sales-bom-template?account=${accountId}&productName=${productName}`
      )
    ).data.payload;
  } catch (error) /* istanbul ignore next */ {
    logError(error as Error);
    throw error;
  }
};

export interface PartsCategoriesSpreadsheetResponse {
  downloadURL: string;
}

export const getPartsCategoriesSpreadsheet = async ({
  productPID,
  configurationPID,
  accountId,
}: {
  productPID: string;
  configurationPID: string;
  accountId: string;
}): Promise<PartsCategoriesSpreadsheetResponse> => {
  const productInventoryService = await getProductInventoryService();

  const url = new URL(
    `${process.env.REACT_APP_PRODUCT_INVENTORY_SERVICE_BASE_URL}${productInventoryEndpoints.products}/${productPID}${productInventoryEndpoints.partCategories}`
  );

  url.searchParams.set('configurationPID', configurationPID);
  url.searchParams.set('account', accountId);

  try {
    const response = await productInventoryService.get(`${url.pathname}${url.search}`, {
      headers: { Accept: 'text/csv' },
    });
    return response.data.payload;
  } catch (error) {
    logError(error as Error);
    throw error;
  }
};

export type SalesBOMJobStatus = 'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | 'CANCELLED_BY_USER';

export interface GETSBOMJobResponse {
  url: string;
  runID: string;
  status: SalesBOMJobStatus;
  created: string;
  errors: ErrorBody[] | null;
  jobType: 'SalesBOM';
}

export type AnyJobResponse = CADUploadJob | SalesBomJob | PartCategoriesJob;

export const getSBOMJob = async (accountId: string, runID: string): Promise<GETSBOMJobResponse> => {
  const productInventoryService = await getProductInventoryService();

  try {
    return (
      await productInventoryService.get(
        `${productInventoryEndpoints.contentProcessing.sbom}/${runID}?account=${accountId}`
      )
    ).data.payload;
  } catch (error) {
    logError(error as Error);
    throw error;
  }
};

export interface GetConfigurationMatchReportResponse {
  successfullyMatched: number;
  unsuccessfullyMatched: number;
  totalPartsUploaded: number;
  matchReportPresignedURL: string;
}

export const getConfigurationMatchReport = async (
  accountId: string,
  configurationPID: string,
  runID?: string
): Promise<GetConfigurationMatchReportResponse> => {
  const productInventoryService = await getProductInventoryService();

  const url = new URL(
    `${process.env.REACT_APP_PRODUCT_INVENTORY_SERVICE_BASE_URL}${productInventoryEndpoints.configurations}/${configurationPID}/match-report`
  );

  url.searchParams.set('account', accountId);

  if (runID !== undefined && runID !== '') {
    url.searchParams.set('salesBOMRunID', runID);
  }

  try {
    const response = await productInventoryService.get(`${url.pathname}${url.search}`);
    return response.data.payload;
  } catch (error) {
    logError(error as Error);
    throw error;
  }
};

export const getConfigurationMatchReportV2 = async (
  accountId: string,
  configurationPID: string,
  runID?: string
): Promise<GetConfigurationMatchReportResponse> => {
  const productInventoryService = await getProductInventoryService();

  const url = new URL(
    `${process.env.REACT_APP_PRODUCT_INVENTORY_SERVICE_BASE_URL}/v2${productInventoryEndpoints.configurations}/${configurationPID}/match-report`
  );

  url.searchParams.set('account', accountId);

  if (runID !== undefined && runID !== '') {
    url.searchParams.set('salesBOMRunID', runID);
  }

  try {
    const response = await productInventoryService.get(`${url.pathname}${url.search}`);
    return response.data.payload;
  } catch (error) {
    logError(error as Error);
    throw error;
  }
};

export const getConfigurationMatchReportV3 = async (
  accountId: string,
  configurationPID: string,
  productName: string,
  runID?: string
): Promise<GetConfigurationMatchReportResponse> => {
  const productInventoryService = await getProductInventoryService();

  const url = new URL(
    `${process.env.REACT_APP_PRODUCT_INVENTORY_SERVICE_BASE_URL}/v3${productInventoryEndpoints.configurations}/${configurationPID}/match-report`
  );

  url.searchParams.set('account', accountId);
  url.searchParams.set('productName', productName);

  if (runID !== undefined && runID !== '') {
    url.searchParams.set('salesBOMRunID', runID);
  }

  try {
    const response = await productInventoryService.get(`${url.pathname}${url.search}`);
    return response.data.payload;
  } catch (error) {
    logError(error as Error);
    throw error;
  }
};

export type ItemType = 'Assembly' | 'Part' | 'Category' | 'Configuration';

export interface CategoryNode {
  object3DName?: string;
  PID: string;
  name: string;
  itemType: ItemType;
  partNumber: string;
  categoryName?: string;
  isCategory?: boolean;
  children: CategoryNode[];
}

export interface GetPartCategoryTreeResponse {
  url: string;
  productPID: string;
  configurationPID: string;
  categories: CategoryNode;
}

export const getPartCategoryTree = async ({
  accountId,
  configurationPID,
  productPID,
  previous,
}: {
  accountId: string;
  configurationPID: string;
  productPID: string;
  previous?: boolean;
}): Promise<GetPartCategoryTreeResponse> => {
  const productInventoryService = await getProductInventoryService();

  const url = new URL(
    `${process.env.REACT_APP_PRODUCT_INVENTORY_SERVICE_BASE_URL}${productInventoryEndpoints.products}/${productPID}${productInventoryEndpoints.partCategories}`
  );

  url.searchParams.set('configurationPID', configurationPID);
  url.searchParams.set('account', accountId);
  url.searchParams.set('categoryFields', ['salesName', 'salesDescription', 'prices', 'itemType'].join(','));

  if (previous) {
    url.searchParams.set('version', 'previous');
  }

  try {
    const response = await productInventoryService.get(`${url.pathname}${url.search}`, {
      headers: { Accept: 'application/json' },
    });
    return response.data.payload;
  } catch (error) {
    logError(error as Error);
    throw error;
  }
};
